import React from 'react';
import { create } from 'zustand';

type TouchedFieldsState = {
  touched: Record<string, Set<string>>;
  isTouched: (fieldId: string, modules: string[]) => boolean;
  touchField: (fieldId: string, modules: string[]) => void;
  touchAll: (fields: string[], modules: string[]) => void;
  resetFields: () => void;
};

export const useTouchedFieldsStore = create<TouchedFieldsState>((set, get) => ({
  touched: {},

  resetFields: () => set({ touched: {} }),

  isTouched: (fieldId, modules) => {
    const setModules = get().touched[fieldId];

    if (!setModules) {
      return false;
    }

    return modules.some((moduleId) => setModules.has(moduleId));
  },

  touchField: (fieldId, modules) => {
    set((state) => {
      const existingSet = state.touched[fieldId];
      const newSet = new Set(existingSet ?? []);

      modules.forEach((moduleId) => newSet.add(moduleId));

      return {
        touched: {
          ...state.touched,
          [fieldId]: newSet,
        },
      };
    });
  },

  touchAll: (fields, modules) => {
    set(() => ({
      touched: fields.reduce<Record<string, Set<string>>>((acc, fieldId) => {
        acc[fieldId] = new Set(modules);
        return acc;
      }, {}),
    }));
  },
}));

export function useTouchedField(fieldId: string, modules: string[]) {
  const touched = useTouchedFieldsStore((state) => state.touched[fieldId]);

  const isTouched = touched ? modules.some((moduleId) => touched.has(moduleId)) : false;

  const touchField = useTouchedFieldsStore((state) => state.touchField);

  const markTouched = React.useCallback(() => {
    touchField(fieldId, modules);
  }, [fieldId, modules, touchField]);

  return { isTouched, markTouched };
}
